// Shared page-body for all Vike scrolly viewer routes.
//
// Plan-1.9 Phase 4 STOPP 4.A.8: introduced to back the four backwards-
// compatible URL patterns (Master-Briefing v5 Z. 435) with a single
// rendering implementation:
//   - `/s/:slug`                            → urlKey=slug, mockFallback=on
//   - `/scrolly/:publicKey`                 → urlKey=publicKey
//   - `/:orgSlug/:scrollySlug`              → urlKey=`<orgSlug>/<scrollySlug>`
//   - `/scrollytelling/:orgSlug/:scrollySlug` → urlKey=`<orgSlug>/<scrollySlug>`
//
// Each Vike page passes its routeParams-derived `urlKey` plus the
// `pageData` from its own `+data.ts` resolver. The mock-fallback path
// (`SCROLLYS_BY_SLUG[slug]`) is opt-in via `mockFallbackKey` and only
// used by `/s/:slug` because the dev feed-mocks are keyed by demo-slug,
// not by publicKey or org+slug pairs.
//
// Renders identical Phase-3 features (renderMode=og, scene-anchor,
// resume-state) for all routes — the route differences are purely in
// data-resolution and URL-display, not in viewer behaviour.

import { BRANDING_DEFAULTS, type BrandingTokens } from "@scrolly/branding";
import { ViewerChrome } from "./ViewerChrome";
import { BrandingProvider } from "./BrandingProvider";
import { NotFound } from "../common/NotFound";
import { LoadError } from "../common/LoadError";
import { ScrollyErrorBoundary } from "../common/ScrollyErrorBoundary";
import { PINK } from "../../lib/tokens";
import type { CategoryId } from "../../lib/categories";
import { useEffect } from "react";
// Plan-N2 (2026-05-25): useViewerMode-Hook nicht mehr aufgerufen
// (Mode-Toggle wurde aus TopBar entfernt). Aber localStorage-Eintrag
// aus früheren Sessions ("scrolly_viewer_mode=immersive") muss
// einmalig gecleared werden — sonst zeigt Bene's Browser weiter
// Immersive-Mode obwohl ViewerChrome jetzt auf "phone" defaultet.
import {
  SCROLLYS_BY_SLUG,
  type MockScrolly,
} from "../../lib/mock/scrollys";
import { extractThumbnailUrl } from "../../lib/cover-thumbnail";
import type { ResolvedScrolly } from "../../lib/api/client";
import type { ResolvedScrollyPageData } from "../../lib/api/resolved-scrolly-page-data";
import { parseSceneAnchor } from "../../lib/url/parseSceneAnchor";
import { formatScrollyDate } from "../../lib/util/format-date";
import type { PageContext } from "vike/types";
import { useViewTracking } from "../../hooks/useViewTracking";
import { useDurationTracking } from "../../hooks/useDurationTracking";

/**
 * Dev-only URL-query overrides for the mock-fallback branding tokens.
 *
 * Phase 13 Commit 12 used this to produce the four smoke screenshots
 * (default / custom-color / google-font / logo-enabled) against the
 * mock scrolly route, because the end-to-end backend fixture is not
 * seeded in this environment. Active only under `import.meta.env.DEV`;
 * in production the query is ignored and BRANDING_DEFAULTS stays.
 *
 * Recognised query params:
 *   `smokeColor=%23FF00FF`      → tokens.colors.primary
 *   `smokeFont=Playfair+Display` → tokens.typography.display.family
 *   `smokeLogo=<url>`           → tokens.logo.light.url
 *   `smokeShow=1`               → tokens.showLogoInViewer
 */
export function applyDevTokenOverrides(
  base: BrandingTokens,
  search: Record<string, string> | undefined,
): BrandingTokens {
  if (!import.meta.env.DEV || !search) return base;
  const color = search["smokeColor"];
  const font = search["smokeFont"];
  const logo = search["smokeLogo"];
  const show = search["smokeShow"] === "1";
  if (!color && !font && !logo && !show) return base;
  return {
    ...base,
    colors: color
      ? { ...base.colors, primary: color, accent: color }
      : base.colors,
    typography: font
      ? {
          ...base.typography,
          display: { ...base.typography.display, family: font },
        }
      : base.typography,
    logo: logo
      ? {
          ...base.logo,
          light: { ...base.logo.light, url: logo },
        }
      : base.logo,
    showLogoInViewer: show ? true : base.showLogoInViewer,
  };
}

/**
 * Build a MockScrolly-shaped object from backend-resolved data so that
 * ViewerChrome (built around MockScrolly) can render real content without
 * a full prop rewrite. Stats/category/creator are sensible defaults — the
 * chrome reads them but they're cosmetic for the backend-driven path.
 *
 * `urlKey` is the URL-derived identifier (slug, publicKey, or composite)
 * used as the synthetic React id. `resolved.publicKey` is the real backend
 * publicKey used for Phase-3.A Resume-State storage key.
 *
 * PostJson-Konvention (CLAUDE.md): `resolved.post` is the api-client-wrapped
 * `PostJson` shape (`{ data: PostBackend }`), wrapped at the client.ts
 * boundary (see `public/src/lib/api/client.ts` fetchResolved). That means:
 *   - `resolved.post.data.title` → PostBackend.title (PostSerialized field)
 *   - `resolved.post.data.data.slides` → PresentationSerialized.slides
 * Passing `resolved.post` straight to `postJson:` keeps the renderer's
 * double-nesting invariant intact.
 */
export function synthScrollyFromResolved(
  urlKey: string,
  resolved: ResolvedScrolly,
): MockScrolly {
  const post = resolved.post.data;
  const creatorName = resolved.orgContact?.name ?? "Scrolly";
  // Sprint-1-Eyebrow-Fix: PostJson.categoryName ist Forward-Compat-Field
  // im post-types-Schema (post.ts:410), heute aber von Backend nicht
  // gesetzt. Fallback "story" rendert in CoverPage als hidden-Category
  // "STORY · A Scrolly". Bene-Entscheidung 2026-05-19 Pfad A.
  // Plan-2-Backlog: Backend-Schema-Erweiterung um categoryName persistent.
  const categoryName = (resolved.post as { categoryName?: string })
    .categoryName;
  return {
    id: urlKey,
    slug: urlKey,
    title: post.title || "Scrolly",
    description: post.description || undefined,
    categoryId: (categoryName ?? "story") as CategoryId,
    creator: {
      handle: creatorName.toLowerCase().replace(/[^a-z0-9]+/g, "-"),
      name: creatorName,
      avatar: PINK,
    },
    cover: {
      palette: "journalism",
      decoration: "headlines",
    },
    // Reader-Cover (2026-06-01): echtes Pro-Scrolly-Titelbild aus dem
    // Backend-JSON (`post` = resolved.post.data = PostBackend, gleiche
    // Shape wie A's BackendScrolly.json). undefined → CoverSection nutzt
    // den konstanten dunklen Fallback. Logik gespiegelt aus Cover-Fix A.
    coverImage: extractThumbnailUrl(post),
    stats: {
      views: 0,
      duration: "",
      likes: 0,
      saves: 0,
    },
    // Sprint-1-Revision 3.5.5-Fix: Backend liefert publishedAt (optional)
    // bzw. createdAt (PostBackend-required). Fallback-Reihenfolge:
    // publishedAt → createdAt → "" (=Sub-Line wird hidden in TopBar).
    //
    // Plan-N2 Block 3 (2026-05-25 Bene): Bisher floss der ISO-String roh
    // ins MockScrolly.publishedAt (`"2026-04-06T21:48:38.657Z"`) — der
    // Colophon zeigte ihn als rohen Timestamp. Jetzt durch den schon
    // existierenden `formatScrollyDate`-Hybrid-Formatter
    // (public/src/lib/util/format-date.ts) gejagt: <30 Tage relativ
    // ("vor 3 Tagen"), sonst absolut ("6. April 2026"). MockScrolly-Type
    // Doc-Comment "Relativ-deutsch für Phase 11" hat das immer schon
    // erwartet — nur die Verdrahtung fehlte.
    publishedAt: formatScrollyDate(post.publishedAt ?? post.createdAt),
    featured: false,
    tags: [],
    source: "native",
    postJson: resolved.post,
    // Plan-1.9 Phase 3.A.CP3 — Resume-State-Storage-Key + scrollyVersion.
    publicKey: resolved.publicKey,
    updatedAt: resolved.updatedAt,
    // Block 2.6 P7 (2026-05-22): Legal-Links für Colophon-Abbinder.
    legalLinks: resolved.legalLinks,
    // Plan-N2 Block 2 (2026-05-25 Bene): Org-Kontakt für Colophon-Contact-
    // Block. Mirror der app/-Pipeline — Backend liefert das schon in
    // ResolvedScrolly.orgContact, wir reichen es nur durch.
    orgContact: resolved.orgContact,
  };
}

export interface ScrollyViewerPageProps {
  /** Vike pageContext (from usePageContext()). */
  pageContext: PageContext;
  /** Discriminated-union result from the route's `+data.ts`. */
  pageData: ResolvedScrollyPageData | undefined;
  /** URL-derived identifier (slug, publicKey, or composite). */
  urlKey: string;
  /**
   * Optional mock-fallback key. Only `/s/:slug` sets this (slug-based feed
   * mocks). Other routes leave it undefined so the not-found path renders
   * straight away when `+data.ts` returns notFound.
   */
  mockFallbackKey?: string;
  /**
   * URL-prefix for the NotFound message ("Kein Scrolly unter <prefix>/<urlKey>").
   * Default `/s` for the legacy short-link pattern.
   */
  notFoundUrlPrefix?: string;
}

/**
 * Shared scrolly viewer page.
 *
 * Wraps the page body in a per-scrolly ErrorBoundary (A-4, Code-Review
 * 2026-05-29) so a render-time crash in the shared renderer shows a readable
 * error card instead of a white screen, and is isolated to this route. The
 * boundary resets on scrolly change (`resetKey={urlKey}`) so navigating to a
 * different scrolly recovers.
 */
export function ScrollyViewerPage(props: ScrollyViewerPageProps) {
  return (
    <ScrollyErrorBoundary resetKey={props.urlKey} context={props.urlKey}>
      <ScrollyViewerPageBody {...props} />
    </ScrollyErrorBoundary>
  );
}

/**
 * Shared scrolly viewer page-body.
 *
 * Dispatches on the discriminated union from `pageData`:
 *   - success → render ViewerChrome with a synthetic MockScrolly built
 *               from the resolved backend data
 *   - notFound → optional mock-fallback (only `/s/:slug`), then <NotFound/>
 *   - error → <LoadError/> with retry
 *
 * Includes Phase-3 features (renderMode=og, scene-anchor) for all routes.
 */
function ScrollyViewerPageBody({
  pageContext,
  pageData,
  urlKey,
  mockFallbackKey,
  notFoundUrlPrefix = "/s",
}: ScrollyViewerPageProps) {
  // Plan-N2 (2026-05-25): Cleanup alten Mode-Toggle-State.
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.removeItem("scrolly_viewer_mode");
      // URL-Param auch aufräumen falls vorhanden
      const url = new URL(window.location.href);
      if (url.searchParams.has("mode")) {
        url.searchParams.delete("mode");
        window.history.replaceState(
          window.history.state,
          "",
          `${url.pathname}${url.search}${url.hash}`,
        );
      }
    } catch {
      // Storage disabled — no-op
    }
  }, []);

  // Variante D (Vorbedingungs-Track STOPP D CP2): ?renderMode=og blendet
  // im ViewerChrome alle UI-Chrome-Elemente aus, damit der Headless-Browser
  // im OG-Image-Backend ein sauberes Vollbild rendern kann.
  //
  // 2026-05-29: Plus ?embedded=1 — Subset-Strip nur für iframe-Overlay
  // aus portal (ViewerOverlay-Component). Strippt TopBar (X-Button +
  // 3-Punkte-Menu — kollidieren mit Overlay-eigenem X), SideActions und
  // OverflowActionsMenu (Engagement-Aktionen kommen vom Parent-Overlay).
  // chromeOverlays (CompanyLogo/Credits/Scroll-Indicator) und TitleOverlay
  // bleiben sichtbar — sind Inhalt, nicht Engagement-Chrome.
  //
  // SSR-verträglich via pageContext.urlParsed.search statt
  // window.location.search.
  const renderMode: "og" | "embedded" | undefined =
    pageContext.urlParsed?.search?.["renderMode"] === "og"
      ? "og"
      : pageContext.urlParsed?.search?.["embedded"] === "1"
        ? "embedded"
        : undefined;

  // Overlay-Stufe-B (2026-06-10): ?chrome=minimal — das Portal-Overlay
  // rendert Like/Save/Share selbst (same-origin, authentifiziert), der
  // iframe zeigt deshalb KEIN eigenes Engagement-Chrome (SideActions +
  // embedded-OverflowActionsMenu). Realität vor Stufe B: der Polish-
  // Subset-Revert (29.05.) zeigte SideActions AUCH embedded — daher die
  // Desktop-Überlappung im Overlay. Standalone-Links (ohne Param)
  // behalten ihr in-viewer-Engagement + Batch-2-Auth-Hydration.
  const chromeMinimal =
    pageContext.urlParsed?.search?.["chrome"] === "minimal";

  // Plan-N2 Block 3 v3 (2026-05-25 Bene): viewportHint kommt aus
  // onRenderHtml (UA-Sniffing) via passToClient. Wird an ViewerChrome
  // weitergereicht, damit useResponsiveViewer's Initial-State auf
  // Mobile bereits "isCompact=true" ist und der weiße Phone-Mockup-
  // Flash beim First-Render entfällt.
  const viewportHint =
    (pageContext as { viewportHint?: "mobile" | "desktop" }).viewportHint ??
    "desktop";
  const initialIsMobile = viewportHint === "mobile";

  // Plan-1.9 Phase 3.B — Open-Graph-Anchor URL-Parsing.
  // Master-Briefing v5 Z. 384-386: `#scene-N` (primär) + `?at=scene-N`
  // (Bot-Fallback). Hash hat Vorrang. 1-indexed in URL — Konvertierung
  // zur 0-indexed Renderer-Position via clampSceneIdxToZeroIndexed.
  const sceneAnchor = parseSceneAnchor(
    pageContext.urlParsed?.hash,
    pageContext.urlParsed?.search,
  );
  const initialSceneIdxOneIndexed = sceneAnchor?.sceneIdxOneIndexed;

  // Sub-Step 3.3 Phase 2 — View-Tracking-Hook auf Page-Level mounten.
  // Triggert genau einen POST /user-scrollys/:pk/view bei 25%-Scroll.
  // Skip wenn renderMode=og (OG-Image-Render).
  // Wichtig: Hook muss VOR den Conditional-Returns gemountet werden, damit
  // Hooks-Order konsistent ist. publicKey ist undefined wenn pageData
  // notFound oder mock-Pfad → Hook macht no-op.
  const resolvedPublicKey =
    pageData?.kind === "success" ? pageData.resolved.publicKey : undefined;
  useViewTracking({
    publicKey: resolvedPublicKey,
    disabled: renderMode === "og",
  });
  // Verweildauer (Heartbeat + sendBeacon) → scrolly_engagements.duration.
  useDurationTracking({
    publicKey: resolvedPublicKey,
    disabled: renderMode === "og",
  });

  // Backend-driven path (primary).
  if (pageData) {
    if (pageData.kind === "success") {
      const scrolly = synthScrollyFromResolved(urlKey, pageData.resolved);
      return (
        <BrandingProvider tokens={pageData.resolved.brandingTokens}>
          <ViewerChrome
            scrolly={scrolly}
            renderMode={renderMode}
            chromeMinimal={chromeMinimal}
            initialSceneIdxOneIndexed={initialSceneIdxOneIndexed}
            publicKey={pageData.resolved.publicKey}
            engagement={pageData.resolved.engagement}
            initialIsMobile={initialIsMobile}
          />
        </BrandingProvider>
      );
    }
    if (pageData.kind === "error") {
      // LoadError renders with platform defaults — no brand-specific styling
      // for the failure screen so the user can recognise it as a system state.
      return (
        <BrandingProvider tokens={BRANDING_DEFAULTS}>
          <LoadError slug={urlKey} />
        </BrandingProvider>
      );
    }
    // pageData.kind === "notFound" falls through to the mock-lookup below.
  }

  // Fallback: mock-only path when +data.ts didn't run or returned notFound,
  // AND the caller opted into mock-fallback via `mockFallbackKey` (only
  // `/s/:slug` does — feed-mocks are slug-keyed).
  if (mockFallbackKey) {
    const mockScrolly = SCROLLYS_BY_SLUG[mockFallbackKey];
    if (mockScrolly) {
      const mockTokens = applyDevTokenOverrides(
        BRANDING_DEFAULTS,
        pageContext.urlParsed?.search,
      );
      return (
        <BrandingProvider tokens={mockTokens}>
          <ViewerChrome
            scrolly={mockScrolly}
            renderMode={renderMode}
            chromeMinimal={chromeMinimal}
            initialSceneIdxOneIndexed={initialSceneIdxOneIndexed}
            initialIsMobile={initialIsMobile}
          />
        </BrandingProvider>
      );
    }
  }

  return (
    <BrandingProvider tokens={BRANDING_DEFAULTS}>
      <NotFound
        eyebrow="Scrolly nicht gefunden"
        headline={
          <>
            Kein Scrolly unter{" "}
            <em style={{ color: PINK, fontStyle: "italic" }}>
              {notFoundUrlPrefix}/{urlKey}
            </em>
            .
          </>
        }
        detail="Der Link ist abgelaufen oder der Scrolly wurde depubliziert."
      />
    </BrandingProvider>
  );
}
